import clsx from 'clsx'
import { StatMetricTile, StatMetricsGrid } from 'components/StatMetricTile/StatMetricTile'
import { format } from 'date-fns'
import { ReactComponent as ArrowSvg } from 'images/icons/arrow_select.svg'
import React, { useState } from 'react'
import type { AutonomicsResourceProfile } from 'utils/interfaces'
import { formatTime } from 'utils/timeUtils'

import style from './ResourceProfileSummary.module.css'

/**
 * Historical consumables / duration hints from backend `resource_profile` (Autonomics).
 * Collapsible to keep mission/scenario pages visually lighter by default.
 */
const ResourceProfileSummary: React.FC<{
  profile?: AutonomicsResourceProfile | null
  hideDirtyWater?: boolean
}> = ({ profile, hideDirtyWater = false }) => {
  const [expanded, setExpanded] = useState(false)

  const th = profile?.thresholds
  const durP90 = profile?.metrics?.duration_s?.p90
  const n = profile?.sample_count
  const asOf = profile?.as_of

  const battery =
    typeof th?.battery_min_remaining_pct === 'number' && Number.isFinite(th.battery_min_remaining_pct)
      ? `${Math.round(th.battery_min_remaining_pct)}%`
      : '—'

  const water =
    typeof th?.clean_water_min_remaining_pct === 'number' && Number.isFinite(th.clean_water_min_remaining_pct)
      ? `${Math.round(th.clean_water_min_remaining_pct)}%`
      : '—'

  const dirtyWater =
    typeof th?.dirty_water_max_remaining_pct === 'number' && Number.isFinite(th.dirty_water_max_remaining_pct)
      ? `${Math.round(th.dirty_water_max_remaining_pct)}%`
      : '—'

  const duration =
    typeof durP90 === 'number' && Number.isFinite(durP90) ? formatTime(Math.round(durP90)) : '—'

  let history = '—'
  if (typeof n === 'number' && n > 0) {
    if (asOf) {
      try {
        history = `${n} runs · ${format(new Date(asOf), 'dd.MM.yyyy')}`
      } catch {
        history = `${n} runs`
      }
    } else {
      history = `${n} runs`
    }
  }

  return (
    <div className={style.root}>
      <button
        type="button"
        className={style.toggler}
        onClick={() => setExpanded(prev => !prev)}
        aria-expanded={expanded}
      >
        <span className={style.togglerLabel}>Consumptions</span>
        <ArrowSvg className={clsx(style.icon, expanded && style.iconExpanded)} />
      </button>

      {expanded && (
        <StatMetricsGrid
          columns={hideDirtyWater ? '1fr 1fr 1fr 1fr' : '1fr 1fr 1fr 1fr 1fr'}
          className={style.metricsGrid}
        >
          <StatMetricTile label="Min battery (start)">{battery}</StatMetricTile>
          <StatMetricTile label="Min clean water (start)">{water}</StatMetricTile>
          {!hideDirtyWater && (
            <StatMetricTile label="Max dirty water (start)">{dirtyWater}</StatMetricTile>
          )}
          <StatMetricTile label="Duration P90">{duration}</StatMetricTile>
          <StatMetricTile label="History">{history}</StatMetricTile>
        </StatMetricsGrid>
      )}
    </div>
  )
}

export default ResourceProfileSummary
